import {AuthSlices} from './slices'
import * as requestFromServer from '../common/cruds'
import {callTypes, catchError, endCall, startCall} from '../common/slices'
import {cleanConfiguration} from '../configurations/slices'
import {
  clearUserFormDataFromSession,
  getTimezone,
  handleNotificationWithToastify,
  sortInspectionCompaniesWithDecentFirst,
} from '../../../app/services/Functional'
import * as dashboard_actions from '../common/actions'

const {actions} = AuthSlices
const API_URL = process.env.REACT_APP_API_BASE_URL

/**
 * Centralized hydration helper for select-workspace / switch-workspace responses.
 * Normalizes the API response into the Redux shape the UI expects.
 *
 * - Merges workspace fields onto the user for UI convenience
 * - Resolves active_inspection_company by company_id (never [0])
 * - Falls back to a minimal company object from workspace when list is missing
 */
export function hydrateWorkspaceAuth(apiData: any, existingUser?: any, existingCompanies?: any[]) {
  const workspace = apiData.workspace || {}
  const apiUser = apiData.user || {}

  const companyId = workspace.company_id || apiUser.company_id
  const companies = apiData.inspection_companies || existingCompanies || undefined
  const companyList = companies || []

  const enrichedUser = {
    ...(existingUser || {}),
    ...apiUser,
    sub: apiUser.sub || apiUser.id || existingUser?.sub || existingUser?.id,
    membership_id: workspace.membership_id || apiUser.membership_id,
    company_id: companyId,
    company_name: workspace.company_name || apiUser.company_name,
    role_id: workspace.role_id || apiUser.role_id,
    role_name: workspace.role_name || apiUser.role_name,
    session_id: workspace.session_id || apiUser.session_id,
    user_email_id:
      apiUser.user_email_id ??
      (existingUser as {user_email_id?: string} | undefined)?.user_email_id,
    emails: apiUser.emails ?? (existingUser as {emails?: unknown} | undefined)?.emails,
  }

  let activeCompany = companyList.find((c: any) => c.id === companyId) || null

  if (!activeCompany && companyId) {
    activeCompany = {
      id: companyId,
      name: workspace.company_name || apiUser.company_name,
      slug: workspace.company_slug,
      membership_id: workspace.membership_id,
    }
  }

  return {
    access_token: apiData.access_token,
    user: enrichedUser,
    inspection_companies: companies,
    active_inspection_company: activeCompany,
  }
}

export const login = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  const loginPayload = {
    email: payload.username || payload.email,
    password: payload.password,
    request_from: 'web',
  }
  return requestFromServer
    .postRequest(`${API_URL}auth/login/`, loginPayload as any)
    .then((response) => {
      return response
    })
    .catch((e) => {
      const {response} = e
      dispatch(endCall({}))
      return response
    })
}

export const handleSetPickerData = (data: any) => (dispatch: any) => {
  dispatch(actions.setPickerData(data))
}

export const selectWorkspace = (membershipId: string, pickerToken: string) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/select-workspace`, {membership_id: membershipId} as any, {
      headers: {Authorization: `Bearer ${pickerToken}`},
    })
    .then((response) => {
      dispatch(endCall({}))
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
      return e.response
    })
}

export const switchWorkspace = (membershipId: string) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/switch-workspace`, {membership_id: membershipId} as any)
    .then((response) => {
      dispatch(endCall({}))
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
      return e.response
    })
}

export const handleSetWorkspaceAuth = (data: any) => (dispatch: any) => {
  dispatch(actions.setWorkspaceAuth(data))
}

export const handleManageUserData = (response: any) => (dispatch: any) => {
  dispatch(actions.manageUserData(response.data.data))
}

export const getUserAccess = () => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/user-access/`)
    .then((response) => {
      if (!response.data.success) {
        const msg =
          (Array.isArray(response.data.message)
            ? response.data.message.join(' ')
            : response.data.message) || 'Unable to load your access permissions.'
        dispatch(endCall({}))
        dispatch(
          catchError({
            error: msg,
            callType: callTypes.action,
          })
        )
        return response.data
      }
      dispatch(actions.userAccess(response.data.data))
      dispatch(endCall({}))
      return response.data
    })
    .catch((error) => {
      dispatch(endCall({}))
      const resData = error?.response?.data
      const raw = resData?.message
      const msg =
        (Array.isArray(raw) ? raw.filter(Boolean).join(' ') : raw) ||
        error?.message ||
        'Unable to load permissions. Please try signing in again.'
      dispatch(
        catchError({
          error: typeof msg === 'string' ? msg : String(msg),
          callType: callTypes.action,
        })
      )
      return {success: false, message: msg}
    })
}

export const getInspectionCompanyById = (id: string, type: string) => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.action}))
  return requestFromServer
    .getRequest(`${process.env.REACT_APP_API_BASE_URL}inspection_companies/${id}/${type}`)
    .then((response) => {
      dispatch(endCall({callType: callTypes.action}))
      dispatch(actions.getCompany(response.data.data))
      return response.data
    })
    .catch((error) => {
      error.clientMessage = "Can't find inspection company"
      dispatch(catchError({error, callType: callTypes.action}))
    })
}

export const getSectorList = () => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.list}))
  return requestFromServer
    .getRequest(`${API_URL}sectors`)
    .then((response) => {
      dispatch(endCall({callType: callTypes.list}))
      dispatch(actions.getSectorList(response.data.data))
    })
    .catch((error) => {
      error.clientMessage = "Can't find sectors"
      dispatch(catchError({error, callType: callTypes.list}))
    })
}

export const getTeamSizeList = () => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.list}))
  return requestFromServer
    .getRequest(`${API_URL}team-size`)
    .then((response) => {
      dispatch(endCall({callType: callTypes.list}))
      dispatch(actions.getTeamSizeList(response.data.data))
    })
    .catch((error) => {
      error.clientMessage = "Can't find sectors"
      dispatch(catchError({error, callType: callTypes.list}))
    })
}

export const handleLoadingState = (payload: any) => (dispatch: any) => {
  return dispatch(actions.setLoadingState(payload))
}

export const createICCompany = (payload: any) => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.action}))
  dispatch(handleLoadingState(true))
  return requestFromServer
    .postRequest(`${API_URL}inspection_companies/company/sign/up`, payload)
    .then((response: any) => {
      dispatch(endCall({callType: callTypes.action}))
      if (response.data.success && response.data.code === 200) {
        dispatch(handleLoadingState(false))
        if (!response.data.data.payment_details) {
          dispatch(actions.getEnterprisePlanDialog(false))
          dispatch(actions.getSuccessDialog(true))
        } else {
          window.location.replace(response.data.data.payment_details)
        }
      } else {
        dispatch(handleLoadingState(false))
        handleNotificationWithToastify(response.data)
      }
      return response.data
    })
    .catch((error) => {
      error.clientMessage = "Can't create inspection company"
      dispatch(catchError({error, callType: callTypes.action}))
      return error
    })
}

export const closeSuccesDialog = (payload: any) => (dispatch: any) => {
  return dispatch(actions.getSuccessDialog(payload))
}

export const handleEnterprisePlanDialog = (payload: any) => (dispatch: any) => {
  return dispatch(actions.getEnterprisePlanDialog(payload))
}

export const getSubscriptionPlanList = () => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.list}))
  return requestFromServer
    .getRequest(`${API_URL}subscription_plans/active/sign-up`)
    .then((response) => {
      dispatch(endCall({callType: callTypes.list}))
      dispatch(actions.getSubscriptionPlanList(response.data.data))
    })
    .catch((error) => {
      error.clientMessage = "Can't find subscription plan"
      dispatch(catchError({error, callType: callTypes.list}))
    })
}

export const verifyOTP = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/validate-otp/`, payload)
    .then((response) => {
      if (!response.data.success) {
        dispatch(catchError({message: response.data.message}))
        dispatch(endCall({}))
        return response.data
      }
      // validate-otp returns {otp_verified: true} — workspace selection issues the token separately
      dispatch(endCall({}))
      return response.data
    })
    .catch((e) => {
      dispatch(endCall({}))
      const resData = e?.response?.data
      const raw = resData?.message
      const msg = (Array.isArray(raw) ? raw.filter(Boolean).join(' ') : raw) || e?.message || 'OTP verification failed.'
      return {success: false, message: msg}
    })
}

export const resendOTP = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/resend-otp/`, payload)
    .then((response) => {
      if (!response.data.success) {
        dispatch(catchError({message: response.data.message}))
        dispatch(endCall({}))
        return response.data
      }
      const od = response?.data?.data?.otpDelivery
      if (od != null) {
        dispatch(actions.setOtpDelivery(od))
      }
      dispatch(endCall({}))
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
      const resData = e?.response?.data
      const status = e?.response?.status ?? 0
      let message = e?.message || 'Unable to reach the server. Check your network or try again.'
      let code = status
      if (resData && typeof resData === 'object') {
        const rawMsg = (resData as {message?: unknown}).message
        const m = Array.isArray(rawMsg) ? rawMsg[0] : rawMsg
        if (typeof m === 'string' && m.length > 0) {
          message = m
        }
        if (typeof (resData as {code?: unknown}).code === 'number') {
          code = (resData as {code: number}).code
        } else if (typeof (resData as {statusCode?: unknown}).statusCode === 'number') {
          code = (resData as {statusCode: number}).statusCode
        }
        dispatch(catchError({message}))
      }
      return {success: false, code, message}
    })
}

export const logout = (navigate: any) => async (dispatch: any) => {
  try {
    await requestFromServer.postRequest(`${API_URL}auth/logout/`)
  } catch (_) {
    // ignore — server-side session revocation is best-effort; clear local state regardless
  }
  dispatch(actions.logout())
  dispatch(cleanConfiguration({}))
  dispatch(dashboard_actions.resetBillingDetails())
  dispatch(dashboard_actions.resetDashboardData())
  dispatch(actions.setIsPageLoading(false))
  dispatch(endCall({}))
  clearUserFormDataFromSession()
  localStorage.removeItem('assetName')
  navigate('/auth/login')
}

export const getToolchainCredits = (id: string) => (dispatch: any) => {
  dispatch(startCall({callType: callTypes.list}))
  return requestFromServer
    .getRequest(`${process.env.REACT_APP_API_BASE_URL}dashboard/tccs/balance?company_id=${id}`)
    .then((response) => {
      dispatch(endCall({callType: callTypes.list}))
      const balance = response.data.data.tcc_balance
      if (balance < Number(process.env.REACT_APP_TCC_BALANCE_THRESHOLD) || !balance) {
        dispatch(actions.setTCCBalanceCardStatus(true))
      } else {
        dispatch(actions.setTCCBalanceCardStatus(false))
      }
      dispatch(actions.getTCCBalance(response.data.data))
    })
    .catch((error) => {
      error.clientMessage = 'Failed to retrieve TCC credits'
      dispatch(catchError({error, callType: callTypes.list}))
    })
}

export const getInspectionCompanies = (id?: string) => (dispatch: any, getState: any) => {
  dispatch(startCall({callType: callTypes.list}))
  return requestFromServer
    .getRequest(
      `${process.env.REACT_APP_API_BASE_URL}auth/get/companies/superadmin/profile/dropdown`
    )
    .then((response) => {
      const data = response.data.data
      const companies = sortInspectionCompaniesWithDecentFirst(data.inspection_companies)
      dispatch(actions.setInspectionCompanies({...data, inspection_companies: companies}))

      if (data.user?.is_root !== undefined) {
        dispatch(actions.updateUser({user: {is_root: data.user.is_root}}))
      }

      const resolveId =
        id || getState().auth.active_inspection_company?.id || getState().auth.user?.company_id
      if (resolveId) {
        const activeCompany = companies.find((company: any) => company.id === resolveId)
        if (activeCompany) {
          dispatch(actions.setActiveCompany(activeCompany))
        }
      }
      return response
    })
    .catch((error) => {
      error.clientMessage = "Can't find a list of inspection companies"
      dispatch(catchError({error, callType: callTypes.list}))
    })
}

export const checkSubscriptionExpiry = (id: string) => (dispatch: any) => {
  dispatch(startCall({}))
  const payloadData = {timezone: getTimezone()}
  return requestFromServer
    .postRequest(`${API_URL}inspection_companies/${id}/check`, payloadData as unknown as null)
    .then((response) => {
      if (!response.data.success) {
        dispatch(catchError({message: response.data.message}))
        return response.data
      }
      dispatch(actions.checkSubscriptionExpiry(response.data.data))
      dispatch(actions.renewCompanyId(id))
      dispatch(endCall({}))
      return response.data
    })
}

export const renewSubscriptionPlan = (id: string) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}inspection_companies/${id}/renew/plan`)
    .then((response) => {
      if (response.data.success && response.data.code === 200) {
        dispatch(handlePageLoading(false))
        dispatch(handleLoadingState(false))
        if (response.data.data.subscription_plan.paymentLink.includes('null')) {
          dispatch(actions.checkSubscriptionExpiry(true))
          dispatch(actions.renewCompanyId(null))
        } else {
          window.location.replace(response.data.data.subscription_plan.paymentLink)
        }
      }
      dispatch(endCall({}))
      return response.data
    })
    .catch((e) => {
      dispatch(endCall({}))
    })
}

export const setSubscriptionExpiry = (data: any) => (dispatch: any) => {
  return dispatch(actions.checkSubscriptionExpiry(data))
}

export const handlePageLoading = (value: boolean) => (dispatch: any) => {
  return dispatch(actions.setIsPageLoading(value))
}

export const resetData = () => (dispatch: any) => {
  dispatch(actions.logout())
}

export const handleDeleteData = () => (dispatch: any) => {
  dispatch(actions.deleteData())
}

export const setIsOTPMode = (value: boolean) => (dispatch: any) => {
  dispatch(actions.setIsOTPMode(value))
}

export const handleIsOTPSentMode = (value: boolean) => (dispatch: any) => {
  dispatch(actions.setIsOTPSentMode(value))
}

export const handleIsInOTPPage = (value: boolean) => (dispatch: any) => {
  dispatch(actions.setIsInOTPPage(value))
}

export const setOtpDelivery = (payload: any) => (dispatch: any) => {
  dispatch(actions.setOtpDelivery(payload))
}

export const handleOTPMode = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  dispatch(actions.setOTPMode(payload.mode))
  return requestFromServer
    .postRequest(`${API_URL}auth/otp-sending-to-email-or-mobile/`, payload)
    .then((response) => {
      // dispatch(resetData())
      dispatch(endCall({}))
      const od = response?.data?.data?.otpDelivery
      if (response?.data?.success && od != null) {
        dispatch(actions.setOtpDelivery(od))
      }
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
      const data = e?.response?.data
      if (data && typeof data === 'object') {
        return {data: e.response.data, status: e.response.status}
      }
      return {
        data: {
          success: false,
          code: e?.response?.status ?? 0,
          message: e?.message || 'Unable to reach the server. Check your network or try again.',
        },
      }
    })
}

export const handleGenerateOTP = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/otp-sending-to-email-or-mobile`, payload)
    .then((response) => {
      dispatch(endCall({}))
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
    })
}

export const handleVerifyOTP = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/company-sign-up-validate-otp`, payload)
    .then((response) => {
      if (!response.data.success) {
        dispatch(catchError({message: response.data.message}))
        return response.data
      }
      dispatch(endCall({}))
      return response.data
    })
    .catch((e) => {
      dispatch(endCall({}))
    })
}

export const handleResendOTP = (payload: any) => (dispatch: any) => {
  return requestFromServer
    .postRequest(`${API_URL}auth/otp-sending-to-email-or-mobile`, payload)
    .then((response) => {
      return response
    })
}

export const getUserById = (id: string, type: string) => (dispatch: any) => {
  return requestFromServer
    .getRequest(`${process.env.REACT_APP_API_BASE_URL}users/${id}/${type}`)
    .then((response) => {
      return response
    })
}

export const handlePhoneNoVerifyDialog = (value: boolean) => (dispatch: any) => {
  return dispatch(actions.setPhoneNoVerifyDialogState(value))
}

export const handleEmailVerifyDialog = (value: boolean) => (dispatch: any) => {
  return dispatch(actions.setEmailVerifyDialogState(value))
}

export const handleSignUPGenerateOTP = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/company-sign-up-otp-sending-to-email-or-mobile`, payload)
    .then((response) => {
      dispatch(endCall({}))
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
    })
}

export const handleUpdateUser = (value: any) => (dispatch: any) => {
  return dispatch(actions.updateUser(value))
}

export const handleDeleteUser = () => (dispatch: any) => {
  return dispatch(actions.deleteUser())
}

export const handleTemporaryPassword = (value: any) => (dispatch: any) => {
  return dispatch(actions.setTemporaryPassword(value))
}

export const handleResetPassword = (payload: any) => (dispatch: any) => {
  dispatch(startCall({}))
  return requestFromServer
    .postRequest(`${API_URL}auth/forgot-password`, payload)
    .then((response) => {
      dispatch(endCall({}))
      handleNotificationWithToastify(response.data)
      return response
    })
    .catch((e) => {
      dispatch(endCall({}))
    })
}

export const handleExpiredCompany = (payload: any) => (dispatch: any) => {
  return dispatch(actions.setExpiredCompany(payload))
}

export const handleIsExpiredCompanyAPIcalling = (payload: any) => (dispatch: any) => {
  return dispatch(actions.setIsExpiredCompanyAPIcalling(payload))
}
